/**
 * SpreadsheetEditor — Collaborative spreadsheet with formulas
 * Uses Yjs SharedMap for cells + HyperFormula for formula evaluation
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HocuspocusProvider } from '@hocuspocus/provider';
import * as Y from 'yjs';

const API = '/api/work/documents';
const COLS = 26; // A-Z
const INITIAL_ROWS = 50;
const COL_LETTERS = Array.from({ length: COLS }, (_, i) => String.fromCharCode(65 + i));

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

function cellKey(row, col) { return `${COL_LETTERS[col]}${row + 1}`; }

export default function SpreadsheetEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [saved, setSaved] = useState(true);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [rows, setRows] = useState(INITIAL_ROWS);
  const [cellValues, setCellValues] = useState({});
  const [computedValues, setComputedValues] = useState({});
  const [cellFormats, setCellFormats] = useState({});
  const [sheetNames, setSheetNames] = useState(['Sheet1']);
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  const editInputRef = useRef(null);
  const formulaBarRef = useRef(null);
  const hfRef = useRef(null);

  const ydoc = useMemo(() => new Y.Doc(), []);
  const cellsMap = useMemo(() => ydoc.getMap('cells'), [ydoc]);
  const formatsMap = useMemo(() => ydoc.getMap('formats'), [ydoc]);
  const sheetsMap = useMemo(() => ydoc.getMap('sheets'), [ydoc]);

  const provider = useMemo(() => {
    if (!id) return null;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/collaboration`;
    return new HocuspocusProvider({
      url: wsUrl, name: `doc:${id}`, document: ydoc,
      token: getCookie('session_token') || 'none',
      onSynced: () => { setSaved(true); syncFromYjs(); },
      onAwarenessUpdate: ({ states }) => {
        const users = [];
        states.forEach(s => { if (s.user) users.push(s.user); });
        setConnectedUsers(users);
      },
    });
  }, [id, ydoc]);

  // Sync Yjs map → local state (filter by active sheet)
  const syncFromYjs = useCallback(() => {
    // Read sheet names from Yjs or auto-detect from cell key prefixes
    const storedNames = sheetsMap.get('names');
    if (storedNames && Array.isArray(storedNames) && storedNames.length > 1) {
      setSheetNames(storedNames);
    } else {
      // Auto-detect sheets from cell keys (prefixed like "1:A1", "2:B3")
      const detectedSheets = new Set();
      detectedSheets.add(0); // sheet 0 always exists
      cellsMap.forEach((_, k) => {
        const match = k.match(/^(\d+):/);
        if (match) detectedSheets.add(parseInt(match[1]));
      });
      if (detectedSheets.size > 1) {
        const names = [...detectedSheets].sort().map((idx, i) => storedNames?.[idx] || `Sheet ${idx + 1}`);
        setSheetNames(names);
      }
    }

    // Filter cells for active sheet (keys: "1:A1" for sheet 1, plain "A1" for sheet 0)
    const prefix = `${activeSheetIdx}:`;
    const vals = {};
    cellsMap.forEach((v, k) => {
      if (k.startsWith(prefix)) vals[k.slice(prefix.length)] = v;
      else if (!k.includes(':') && activeSheetIdx === 0) vals[k] = v;
    });
    setCellValues(vals);
    const fmts = {};
    formatsMap.forEach((v, k) => {
      if (k.startsWith(prefix)) fmts[k.slice(prefix.length)] = v;
      else if (!k.includes(':') && activeSheetIdx === 0) fmts[k] = v;
    });
    setCellFormats(fmts);
    recalcFormulas(vals);
  }, [cellsMap, formatsMap, sheetsMap, activeSheetIdx]);

  // Listen for Yjs changes
  useEffect(() => {
    const observer = () => syncFromYjs();
    cellsMap.observe(observer);
    formatsMap.observe(observer);
    return () => { cellsMap.unobserve(observer); formatsMap.unobserve(observer); };
  }, [cellsMap, formatsMap, syncFromYjs]);

  // Re-sync when active sheet changes
  useEffect(() => {
    syncFromYjs();
  }, [activeSheetIdx, syncFromYjs]);

  // Initialize HyperFormula
  useEffect(() => {
    let hf;
    (async () => {
      try {
        const { HyperFormula } = await import('hyperformula');
        hf = HyperFormula.buildEmpty({ licenseKey: 'gpl-v3' });
        hf.addSheet('Sheet1');
        hfRef.current = hf;
      } catch (err) { console.warn('HyperFormula init failed:', err); }
    })();
    return () => { if (hf) hf.destroy(); };
  }, []);

  // Recalculate formulas
  const recalcFormulas = useCallback((vals) => {
    const hf = hfRef.current;
    if (!hf) { setComputedValues(vals); return; }

    try {
      // Build a data grid from vals
      const maxRow = Math.max(rows, ...Object.keys(vals).map(k => parseInt(k.replace(/[A-Z]/g, '')) || 0));
      const grid = [];
      for (let r = 0; r < maxRow; r++) {
        const row = [];
        for (let c = 0; c < COLS; c++) {
          const key = cellKey(r, c);
          const v = vals[key];
          row.push(v !== undefined ? v : null);
        }
        grid.push(row);
      }

      // Replace sheet content
      const sheetName = hf.getSheetName(0);
      if (sheetName) {
        const sheetId = hf.getSheetId(sheetName);
        if (sheetId !== undefined) {
          hf.setSheetContent(sheetId, grid);
        }
      }

      // Read computed values
      const computed = { ...vals };
      for (const key of Object.keys(vals)) {
        const v = vals[key];
        if (typeof v === 'string' && v.startsWith('=')) {
          const colStr = key.replace(/[^A-Z]/g, '');
          const col = colStr.charCodeAt(0) - 65;
          const row = parseInt(key.replace(/[A-Z]/g, '')) - 1;
          try {
            const result = hf.getCellValue({ sheet: 0, row, col });
            computed[key] = result !== null && typeof result === 'object' ? (result.value ?? result) : result;
          } catch { computed[key] = v; }
        }
      }
      setComputedValues(computed);
    } catch (err) {
      console.warn('Formula recalc error:', err);
      setComputedValues(vals);
    }
  }, [rows]);

  // Load document metadata
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/${id}`, { credentials: 'include' });
        const data = await res.json();
        setDoc(data.document);
        setTitle(data.document?.title || 'Untitled Spreadsheet');

        // Inject initial content from file conversion
        const ic = data.document?.settings?.initialContent;
        if (ic?.cells) {
          setTimeout(() => {
            if (cellsMap.size === 0) {
              ydoc.transact(() => {
                for (const [key, val] of Object.entries(ic.cells)) {
                  cellsMap.set(key, val);
                }
                if (ic.sheetNames && ic.sheetNames.length > 0) {
                  sheetsMap.set('names', ic.sheetNames);
                  setSheetNames(ic.sheetNames);
                }
              });
              // Clear initialContent
              fetch(`${API}/${id}`, { method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings: {} }) }).catch(() => {});
            }
          }, 500);
        }
      } catch { navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work'); }
      finally { setLoading(false); }
    })();
    return () => { provider?.destroy(); ydoc.destroy(); };
  }, [id]);

  const setCellValue = (key, value) => {
    const fullKey = activeSheetIdx === 0 ? key : `${activeSheetIdx}:${key}`;
    if (value === '' || value === null || value === undefined) {
      cellsMap.delete(fullKey);
    } else {
      cellsMap.set(fullKey, value);
    }
    setSaved(false);
    clearTimeout(window._sheetSaveTimer);
    window._sheetSaveTimer = setTimeout(() => setSaved(true), 2000);
  };

  const handleCellClick = (row, col) => {
    const key = cellKey(row, col);
    if (selectedCell === key) {
      // Already selected — start editing
      setEditingCell(key);
      setEditValue(cellValues[key] !== undefined ? String(cellValues[key]) : '');
    } else {
      setSelectedCell(key);
      setEditingCell(null);
    }
  };

  const handleCellDoubleClick = (row, col) => {
    const key = cellKey(row, col);
    setSelectedCell(key);
    setEditingCell(key);
    setEditValue(cellValues[key] !== undefined ? String(cellValues[key]) : '');
  };

  const commitEdit = () => {
    if (editingCell) {
      const val = editValue.trim();
      // Try to parse as number
      const num = Number(val);
      setCellValue(editingCell, val.startsWith('=') ? val : (!isNaN(num) && val !== '' ? num : val));
    }
    setEditingCell(null);
  };

  const handleKeyDown = (e) => {
    if (!selectedCell) return;
    const col = selectedCell.charCodeAt(0) - 65;
    const row = parseInt(selectedCell.slice(1)) - 1;

    if (e.key === 'Enter') {
      if (editingCell) { commitEdit(); setSelectedCell(cellKey(Math.min(row + 1, rows - 1), col)); }
      else { handleCellDoubleClick(row, col); }
      e.preventDefault();
    } else if (e.key === 'Tab') {
      if (editingCell) commitEdit();
      setSelectedCell(cellKey(row, Math.min(col + 1, COLS - 1)));
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    } else if (!editingCell && e.key === 'ArrowDown') { setSelectedCell(cellKey(Math.min(row + 1, rows - 1), col)); }
    else if (!editingCell && e.key === 'ArrowUp') { setSelectedCell(cellKey(Math.max(row - 1, 0), col)); }
    else if (!editingCell && e.key === 'ArrowRight') { setSelectedCell(cellKey(row, Math.min(col + 1, COLS - 1))); }
    else if (!editingCell && e.key === 'ArrowLeft') { setSelectedCell(cellKey(row, Math.max(col - 1, 0))); }
    else if (!editingCell && e.key === 'Delete') { setCellValue(selectedCell, null); }
    else if (!editingCell && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      setEditingCell(selectedCell);
      setEditValue(e.key);
    }
  };

  async function handleTitleChange(newTitle) {
    setTitle(newTitle);
    try {
      await fetch(`${API}/${id}`, { method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: newTitle }) });
    } catch {}
  }

  if (loading) {
    return <div className="flex h-screen items-center justify-center bg-white">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-green-500" />
    </div>;
  }

  const displayValue = (key) => {
    const raw = cellValues[key];
    const computed = computedValues[key];
    if (computed !== undefined && computed !== raw) return String(computed);
    if (raw !== undefined && raw !== null) return String(raw);
    return '';
  };

  return (
    <div className="flex h-screen flex-col bg-white" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Header */}
      <header className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-4 py-2 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button onClick={() => navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work')} className="rounded p-1.5 text-gray-400 hover:bg-gray-100 flex-shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <input type="text" value={title} onChange={e => handleTitleChange(e.target.value)}
            className="border-none bg-transparent text-base sm:text-lg font-semibold text-gray-900 focus:outline-none flex-1 min-w-0" placeholder="Untitled Spreadsheet" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex -space-x-2">
            {connectedUsers.slice(0, 3).map((u, i) => (
              <div key={i} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 border-white text-[10px] font-bold text-white flex items-center justify-center"
                style={{ backgroundColor: u.color || '#059669' }} title={u.name}>{(u.name||'?')[0].toUpperCase()}</div>
            ))}
          </div>
          <span className={`text-xs hidden sm:inline ${saved ? 'text-gray-400' : 'text-amber-500'}`}>{saved ? 'Saved' : 'Saving...'}</span>
        </div>
      </header>

      {/* Formula bar */}
      <div className="flex items-center border-b border-gray-200 px-2 py-1 gap-2 flex-shrink-0 bg-gray-50">
        <span className="text-xs font-mono text-gray-500 w-10 text-center font-bold">{selectedCell || ''}</span>
        <div className="w-px h-5 bg-gray-300" />
        <input ref={formulaBarRef} type="text" value={editingCell ? editValue : (cellValues[selectedCell] ?? '')}
          onChange={e => { if (editingCell) setEditValue(e.target.value); else if (selectedCell) { setEditingCell(selectedCell); setEditValue(e.target.value); } }}
          onKeyDown={e => { if (e.key === 'Enter') { commitEdit(); e.preventDefault(); } }}
          className="flex-1 text-sm font-mono border-none bg-transparent focus:outline-none" placeholder="Enter value or formula (=SUM(A1:A10))" />
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto">
        <table className="border-collapse w-full" style={{ tableLayout: 'fixed' }}>
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="w-10 bg-gray-100 border border-gray-200 text-[10px] text-gray-500 font-medium sticky left-0 z-20"></th>
              {COL_LETTERS.map(letter => (
                <th key={letter} className="bg-gray-100 border border-gray-200 text-[10px] text-gray-500 font-medium py-1 min-w-[80px] w-[100px]">{letter}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r}>
                <td className="bg-gray-50 border border-gray-200 text-[10px] text-gray-400 text-center font-medium sticky left-0 z-10 w-10">{r + 1}</td>
                {COL_LETTERS.map((_, c) => {
                  const key = cellKey(r, c);
                  const isSelected = selectedCell === key;
                  const isEditing = editingCell === key;
                  return (
                    <td key={c}
                      className={`border border-gray-200 p-0 relative ${isSelected ? 'outline outline-2 outline-blue-500 z-10' : ''}`}
                      onClick={() => handleCellClick(r, c)}
                      onDoubleClick={() => handleCellDoubleClick(r, c)}>
                      {isEditing ? (
                        <input ref={editInputRef} autoFocus type="text" value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={e => { if (e.key === 'Enter') { commitEdit(); e.preventDefault(); } if (e.key === 'Escape') setEditingCell(null); }}
                          className="w-full h-full px-1.5 py-0.5 text-sm border-none outline-none bg-white font-mono" />
                      ) : (
                        <div className={`px-1.5 py-0.5 text-sm truncate min-h-[24px] ${
                          typeof computedValues[key] === 'number' ? 'text-right font-mono' : ''
                        } ${cellFormats[key]?.bold ? 'font-bold' : ''} ${cellFormats[key]?.italic ? 'italic' : ''}`}
                          style={{ color: cellFormats[key]?.color, backgroundColor: cellFormats[key]?.bg }}>
                          {displayValue(key)}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Add rows button */}
        <button onClick={() => setRows(r => r + 50)} className="w-full py-2 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-50">
          + Add 50 rows
        </button>
      </div>

      {/* Sheet tabs */}
      {sheetNames.length >= 1 && (
        <div className="flex items-center border-t border-gray-200 bg-gray-50 px-2 py-1 gap-1 flex-shrink-0">
          {sheetNames.map((name, i) => (
            <button key={i} onClick={() => setActiveSheetIdx(i)}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg border border-b-0 transition-colors ${
                activeSheetIdx === i ? 'bg-white text-gray-900 border-gray-200' : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-200'
              }`}>
              {name}
            </button>
          ))}
          <button onClick={() => {
            const newName = `Sheet${sheetNames.length + 1}`;
            const updated = [...sheetNames, newName];
            setSheetNames(updated);
            sheetsMap.set('names', updated);
            setActiveSheetIdx(updated.length - 1);
          }} className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded">+</button>
        </div>
      )}
    </div>
  );
}
